import React, { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import {
  FaCalendarDays,
  FaSun,
  FaPersonHiking,
  FaChevronDown,
  FaCheck,
  FaArrowRight,
  FaLocationDot,
  FaPhone,
  FaChevronLeft,
  FaChevronRight,
  FaArrowLeft
} from "react-icons/fa6";
import { getOnePackage, getPackages } from "../../slice/khajurahoPackageSlice";
import { addBooking, resetBooking } from "../../slice/bookingSlice";
import { getMediaUrl } from "../../common/mediaUrl";

/* ── Expandable Text with See More / See Less ── */
const ExpandableText = ({ label, text }) => {
  const [expanded, setExpanded] = useState(false);
  const words = text ? text.split(" ") : [];
  const isLong = words.length > 8;
  const preview = isLong ? words.slice(0, 8).join(" ") + "..." : text;

  return (
    <div>
      <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">{label}</p>
      <p className="text-sm font-bold text-gray-dark leading-snug">
        {expanded ? text : preview}
      </p>
      {isLong && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-[10px] font-black uppercase tracking-widest text-orange-main mt-0.5 hover:underline"
        >
          {expanded ? "See Less" : "See More"}
        </button>
      )}
    </div>
  );
};

const TourDetails = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { oneData: tour, loading } = useSelector((state) => state.khajurahoPackage);
  const { success: showSuccess, loading: isSubmitting } = useSelector((state) => state.booking);
  const galleryRef = useRef(null);

  const [activeDay, setActiveDay] = useState(0);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    date: "",
    travelers: "1",
    message: ""
  });

  useEffect(() => {
    if (tour) {
      setFormData(prev => ({
        ...prev,
        message: ` ${tour.name}`
      }));
    }
  }, [tour]);

  useEffect(() => {
    dispatch(getOnePackage(id));
    return () => dispatch(resetBooking());
  }, [dispatch, id]);

  useEffect(() => {
    if (showSuccess) {
      // Clear form immediately
      setFormData({
        name: "",
        phone: "",
        email: "",
        address: "",
        date: "",
        travelers: "1",
        message: tour ? ` ${tour.name}` : ""
      });

      const timer = setTimeout(() => {
        dispatch(resetBooking());
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showSuccess, dispatch, tour]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Restrict phone number to 10 digits and only numbers
    if (name === "phone") {
      const onlyNums = value.replace(/\D/g, "").slice(0, 10);
      setFormData(prev => ({ ...prev, [name]: onlyNums }));
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleBookingSubmit = (e) => {
    e.preventDefault();
    if (formData.phone.length !== 10) {
      alert("Please enter a valid 10-digit mobile number.");
      return;
    }

    const payload = {
      name: formData.name,
      email: formData.email,
      number: formData.phone,
      city: formData.address,
      travelers: parseInt(formData.travelers),
      travelDate: formData.date,
      message: formData.message
    };

    dispatch(addBooking(payload));
  };

  if (loading && !tour) {
    return (
      <div className="py-40 text-center flex justify-center items-center">
        <div className="w-12 h-12 border-4 border-orange-main border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!tour) {
    return (
      <div className="py-40 text-center">
        <h2 className="text-2xl font-bold text-gray-400 italic">Tour Not Found</h2>
        <Link to="/tours" className="mt-4 text-orange-main font-bold hover:underline">Return to Packages</Link>
      </div>
    );
  }

  // Parse images
  let images = [];
  try {
    images = JSON.parse(tour.multipleImages || "[]");
  } catch (e) {
    images = tour.multipleImages ? tour.multipleImages.split(',') : [];
  }
  const heroImage = images.length > 0 ? getMediaUrl(images[0]) : null;

  // ── Build gallery from CURRENT package only ──
  const galleryImages = images.map((img) => ({
    src: getMediaUrl(img),
    pkgName: tour.name
  }));

  const scrollGallery = (dir) => {
    if (galleryRef.current) {
      galleryRef.current.scrollBy({ left: dir === "left" ? -340 : 340, behavior: "smooth" });
    }
  };

  return (
    <div className="bg-white min-h-screen">

      {/* 1. HERO SECTION */}
      <section className="relative h-[280px] sm:h-[350px] lg:h-[400px] flex items-center pt-16 lg:pt-20">
        <div className="absolute inset-0 z-0">
          <img
            src={heroImage}
            className="w-full h-full object-cover"
            alt={tour.name}
          />
          <div className="absolute inset-0 bg-black/60" />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10 w-full">
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/80 hover:text-orange-main transition-colors mb-6 group"
          >
            <div className="w-8 h-8 rounded-full border border-white/20 flex items-center justify-center group-hover:border-orange-main transition-colors">
              <FaArrowLeft className="text-xs" />
            </div>
            <span className="text-[10px] font-black uppercase tracking-widest">Go Back</span>
          </motion.button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-2 mb-4">
              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-orange-main">Tour Details</span>
              <div className="w-1 h-1 rounded-full bg-white/30" />
              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white/70">{tour.durationText}</span>
            </div>

            <h1 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-white tracking-tighter mb-4 lg:mb-6">
              {tour.name}
            </h1>

            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 text-xs font-bold text-white bg-white/10 px-4 py-2 rounded-xl border border-white/10">
                <FaLocationDot className="text-orange-main" />
                {tour.location}
              </div>
              <div className="px-4 py-2 rounded-xl bg-orange-main text-white text-[10px] font-black uppercase tracking-widest">
                {tour.tag || "Popular"}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. MAIN CONTENT */}
      <section className="py-10 lg:py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">

            {/* LEFT CONTENT */}
            <div className="lg:col-span-2 space-y-16">
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-[2px] w-8 bg-orange-main" />
                  <span className="text-[10px] font-black uppercase tracking-[0.2em] text-orange-main">Overview</span>
                </div>
                <h2 className="text-2xl lg:text-3xl font-black text-gray-dark mb-4 lg:mb-6 tracking-tight">Tour Overview</h2>
                <p className="text-gray-500 text-base lg:text-lg leading-relaxed font-medium">
                  {tour.description}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
                  <div className="flex items-center gap-4 p-5 rounded-2xl bg-gray-50 border border-gray-100">
                    <div className="text-orange-main text-xl"><FaCalendarDays /></div>
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Duration</p>
                      <p className="text-sm font-bold text-gray-dark">{tour.durationText}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-5 rounded-2xl bg-gray-50 border border-gray-100">
                    <div className="text-orange-main text-xl"><FaSun /></div>
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Best Time</p>
                      <p className="text-sm font-bold text-gray-dark">{tour.bestTimeText || "October to March"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-5 rounded-2xl bg-gray-50 border border-gray-100">
                    <div className="text-orange-main text-xl"><FaPersonHiking /></div>
                    <ExpandableText label="Activities" text={tour.activityText || "Sightseeing"} />
                  </div>
                </div>
              </div>

              {/* ITINERARY */}
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-2 px-4">
                  <div className="h-[2px] w-8 bg-orange-main" />
                  <span className="text-[10px] font-black uppercase tracking-[0.2em] text-orange-main">Day-wise Itinerary</span>
                </div>
                {tour.itineraries && tour.itineraries.length > 0 ? (
                  tour.itineraries.map((item, idx) => {
                    const isOpen = activeDay === idx;
                    return (
                      <motion.div
                        key={idx}
                        className={`rounded-3xl transition-all duration-300 border-2 ${isOpen ? "bg-white border-orange-main shadow-lg shadow-orange-main/5" : "bg-white border-gray-100"}`}
                      >
                        <button
                          onClick={() => setActiveDay(isOpen ? null : idx)}
                          className="w-full flex justify-between items-center px-4 sm:px-8 py-4 sm:py-6 group focus:outline-none"
                        >
                          <div className="flex items-center gap-3 sm:gap-6">
                            <div className={`w-9 h-9 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl flex items-center justify-center font-black text-xs sm:text-sm transition-all duration-300 flex-shrink-0 ${isOpen ? "bg-orange-main text-white" : "bg-gray-100 text-gray-400"}`}>
                              {item.day}
                            </div>
                            <div className="text-left">
                              <h3 className={`text-sm sm:text-base font-bold tracking-tight transition-colors ${isOpen ? "text-orange-main" : "text-gray-dark"}`}>{item.title}</h3>
                            </div>
                          </div>
                          <FaChevronDown className={`text-xs transition-transform duration-300 ${isOpen ? "rotate-180 text-orange-main" : "text-gray-300"}`} />
                        </button>

                        <AnimatePresence>
                          {isOpen && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="px-4 sm:px-8 pb-6 sm:pb-8 sm:pl-[6.5rem]"
                            >
                              <div className="h-px w-full bg-gray-100 mb-6" />
                              <p className="text-gray-500 font-medium text-base leading-relaxed">
                                {item.description}
                              </p>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    );
                  })
                ) : (
                  <p className="px-4 text-gray-400 italic">Itinerary details coming soon...</p>
                )}
              </div>
            </div>

            {/* RIGHT SIDEBAR */}
            <div className="sticky top-32">
              <div className="bg-white border border-gray-100 rounded-[2rem] shadow-sm relative overflow-hidden">
                <div className="px-8 py-6 border-b border-gray-50">
                  <h3 className="text-xl font-black text-gray-dark tracking-tight">Book Your Package</h3>
                </div>

                <div className="p-8 relative min-h-[400px] flex flex-col justify-center">
                  <AnimatePresence mode="wait">
                    {showSuccess ? (
                      <motion.div
                        key="success"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="text-center py-10"
                      >
                        <div className="w-20 h-20 bg-orange-main/10 rounded-full flex items-center justify-center mx-auto mb-6">
                          <FaCheck className="text-3xl text-orange-main" />
                        </div>
                        <h4 className="text-2xl font-black text-gray-dark mb-2 tracking-tight">Enquiry Sent!</h4>
                        <p className="text-gray-500 text-sm font-medium leading-relaxed">
                          Our travel expert will get back to you shortly.
                        </p>
                        <button
                          onClick={() => dispatch(resetBooking())}
                          className="mt-8 w-full py-4 bg-orange-main/10 text-orange-main rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-orange-main hover:text-white transition-all"
                        >
                          Fill Form Again
                        </button>
                      </motion.div>
                    ) : (
                      <form onSubmit={handleBookingSubmit} className="space-y-4">
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="Your Name"
                          required
                          className="w-full border border-gray-200 rounded-2xl p-4 text-gray-dark font-medium text-sm bg-white focus:outline-none focus:border-orange-main transition-all"
                        />
                        <input
                          type="text"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="Enter number"
                          required
                          maxLength={10}
                          className="w-full border border-gray-200 rounded-2xl p-4 text-gray-dark font-medium text-sm bg-white focus:outline-none focus:border-orange-main transition-all"
                        />
                        <div className="grid grid-cols-2 gap-4">
                          <input
                            type="date"
                            name="date"
                            min={new Date().toISOString().split('T')[0]}
                            value={formData.date}
                            onChange={handleInputChange}
                            required
                            className="w-full border border-gray-200 rounded-2xl p-4 text-gray-dark font-medium text-sm bg-white focus:outline-none focus:border-orange-main"
                          />
                          <input
                            type="number"
                            name="travelers"
                            min="1"
                            value={formData.travelers}
                            onChange={handleInputChange}
                            placeholder="Travelers"
                            required
                            className="w-full border border-gray-200 rounded-2xl p-4 text-gray-dark font-medium text-sm bg-white focus:outline-none"
                          />
                        </div>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="Email ID"
                          className="w-full border border-gray-200 rounded-2xl p-4 text-gray-dark font-medium text-sm bg-white focus:outline-none"
                        />
                        <input
                          type="text"
                          name="address"
                          value={formData.address}
                          onChange={handleInputChange}
                          placeholder="Address / City"
                          className="w-full border border-gray-200 rounded-2xl p-4 text-gray-dark font-medium text-sm bg-white focus:outline-none"
                        />
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleInputChange}
                          placeholder="Your Message / Requirements"
                          rows="3"
                          className="w-full border border-gray-200 rounded-2xl p-4 text-gray-dark font-medium text-sm bg-white focus:outline-none focus:border-orange-main transition-all resize-none"
                        />
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="w-full bg-orange-main text-white text-base font-bold py-3.5 rounded-2xl hover:bg-orange-light shadow-lg shadow-orange-main/10 transition-all active:scale-95 uppercase tracking-widest flex items-center justify-center gap-3"
                        >
                          {isSubmitting ? 'Sending...' : 'Book Tour Now'}
                        </button>
                      </form>
                    )}
                  </AnimatePresence>
                </div>

                <div className="bg-gray-50/50 p-8 border-t border-gray-50 text-center">
                  <div className="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center mx-auto mb-4 border border-gray-100">
                    <FaPhone className="text-xl text-orange-main" />
                  </div>
                  <h4 className="text-lg font-bold text-gray-dark">Need <span className="text-orange-main">Help?</span></h4>
                  <a href="tel:+919993910094" className="block text-2xl font-black text-gray-dark mt-2 hover:text-orange-main transition-colors tracking-tight">
                    +91 99939 10094
                  </a>
                  <a href="tel:+919981980094" className="block text-2xl font-black text-gray-dark mt-2 hover:text-orange-main transition-colors tracking-tight">
                    +91 99819 80094
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── PHOTO GALLERY SLIDER ── */}
      {galleryImages.length > 0 && (
        <section className=" pt-12 pb-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">

            {/* Header */}
            <div className="flex items-end justify-between mb-6 lg:mb-8">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="h-[2px] w-8 bg-orange-main" />
                  <span className="text-[10px] font-black uppercase tracking-[0.3em] text-orange-main">Our Gallery</span>
                </div>
                <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-black tracking-tight">
                  Explore <span className="text-orange-main">More Moments</span>
                </h2>
                {galleryImages.length > 2 && (
                  <p className="text-gray-400 text-xs mt-1 lg:hidden italic">Swipe to explore →</p>
                )}
              </div>
              {/* Arrows – desktop only */}
              {galleryImages.length > 4 && (
                <div className="hidden lg:flex gap-3">
                  <button
                    onClick={() => scrollGallery("left")}
                    className="w-11 h-11 rounded-full border border-orange-main/20 bg-white/10 text-black flex items-center justify-center hover:bg-orange-main hover:border-orange-main transition-all"
                  >
                    <FaChevronLeft />
                  </button>
                  <button
                    onClick={() => scrollGallery("right")}
                    className="w-11 h-11 rounded-full border border-orange-main/20 bg-white/10 text-black flex items-center justify-center hover:bg-orange-main hover:border-orange-main transition-all"
                  >
                    <FaChevronRight />
                  </button>
                </div>
              )}
            </div>

            {/* Slider Track */}
            <div
              ref={galleryRef}
              className="flex gap-3 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-12"
            >
              {galleryImages.map((item, i) => (
                <div
                  key={i}
                  className="snap-start flex-shrink-0 group relative rounded-2xl overflow-hidden bg-gray-800 shadow-lg min-w-[calc(50%-6px)] lg:min-w-[calc(25%-9px)] h-[180px] sm:h-[200px] lg:h-[240px]"
                >
                  <img
                    src={item.src}
                    alt={item.pkgName}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  {/* Always visible subtle gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                  {/* Package name - always visible at bottom */}
                  {/* <div className="absolute bottom-0 left-0 right-0 p-3">
                    <p className="text-white text-[9px] font-black uppercase tracking-widest line-clamp-1 opacity-80">{item.pkgName}</p>
                  </div> */}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

    </div>
  );
};

export default TourDetails;
